import _ from 'lodash'
import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { Cell } from 'fixed-data-table-2'
// @ts-ignore The CI server isn't picking up the namespace definition in this repo. Need to investigate.
import {
  CheckboxField,
  Icon,
  utils,
  InfoPill,
  constants,
  CopyButton,
} from '@adc/ui-core'
import classnames from 'classnames/bind'
import {
  formatMoney,
  formatMoneyWithCents,
  formatPhoneNumber,
} from './utils/formatters'
import * as date from './utils/date'

import styles from './builtinCells.scss'
const cx = classnames.bind(styles)

const { ColorNames } = constants

const COLORED_CELL_OPTIONS = {
  DEFAULT: ColorNames.Gray,
  INFO: ColorNames.Info,
  SUCCESS: ColorNames.Success,
  CAUTION: ColorNames.Caution,
  ERROR: ColorNames.Error,
  INACTIVE: ColorNames.GrayMediumLight,
}

// maps regular color to light
const LIGHT_COLOR_MAPPING = {
  [COLORED_CELL_OPTIONS.DEFAULT]: ColorNames.GrayExtraLight,
  [COLORED_CELL_OPTIONS.INFO]: ColorNames.InfoLight,
  [COLORED_CELL_OPTIONS.SUCCESS]: ColorNames.SuccessLight,
  [COLORED_CELL_OPTIONS.CAUTION]: ColorNames.CautionExtraLight,
  [COLORED_CELL_OPTIONS.ERROR]: ColorNames.ErrorLight,
  [COLORED_CELL_OPTIONS.INACTIVE]: ColorNames.OffWhite,
}

enum NamedRowIndex {
  header = -1,
  footer = -2,
}

enum SORT_DIRECTION {
  ASC = 'asc',
  DESC = 'desc',
}

type CellValue = string | number | null

interface CellProps {
  value?: CellValue
  columnKey: string
  rowIndex: number | NamedRowIndex
  // rowData?: Record<string, any>
  classes?: string[]
  // This is probably better thought of as minWidth size it can be resizable
  width?: number
  children: React.ReactNode
  dataValue?: CellValue
  style?: React.CSSProperties
  truncate?: boolean
}

interface BaseCellProps extends CellProps {
  // Support custom passed down props for specific cell implementations
  [key: string]: unknown
}

interface ExistingColumnProps {
  initialSortDirection: SORT_DIRECTION
}

interface ColumnDefinition {
  colProps: ExistingColumnProps
  dataType?: string
}

const formatPercentage = (
  val: CellValue,
  maximumFractionDigits = 2,
  withSymbol = false
): CellValue => {
  const symbol = withSymbol ? '%' : ''

  if (!val) return ''

  return `${_.round(Number(val) * 100.0, maximumFractionDigits)}${symbol}`
}

const formatBool = (
  val: unknown,
  truthyVal: React.ReactNode = 'Yes',
  falsyVal: React.ReactNode = 'No'
): React.ReactNode => (val ? truthyVal : falsyVal)

type ChangedColumnSort = {
  columnKey: string
}

type ColumnSortChanger = (
  sortedColumns: ChangedColumnSort[],
  sortAscending: boolean,
  dataType?: string
) => void

type HeaderCellProps = Omit<BaseCellProps, 'rowIndex' | 'value'> & {
  isSortableColumn: boolean
  definition: ColumnDefinition
  onSortChange: ColumnSortChanger
  children: React.ReactNode
}

/**
 *
 *                        YOU WANT CELL?
 *
 * ...........~:..................................:~...........
 * ..........+?7+................................+7?+..........
 * ..........7.II+..............................+II.7..........
 * .........=I.OII=............................=IIO.I=.........
 * .........I=.?7+7:..........................:7+7?.=I.........
 * .........7~.++++7..........................7++++.~7.........
 * .........$:.,$.??I........................I??.$,.:$.........
 * .........$:..7~,I7=......................=7I,~7..:$.........
 * .........$:..~$.=I7:....................:7I=.$~..:$.........
 * .........$:...Z~.I?7....................7?I.~Z...:$.........
 * .........$:...=Z.,7??.....:~=++=~:.....??7,.Z=...:$.........
 * .........7=....Z~.=77~.=II?+====+?II=.~77=.~Z....=7.........
 * .........I?....=Z..I7O7?:..........:?7O7I..Z=....?I.........
 * .........=7.....Z+.~D+................+D~.+Z.....7=.........
 * .........:Z.....:O+Z:..................:Z+O:.....Z:.........
 * ..........Z,.....7D......................D7.....,Z..........
 * ..........$=.....,O,....................,O,.....=$..........
 * ..........?I......+Z....................Z+......I?..........
 * ..........,Z.......$?..................?$.......Z,..........
 * ...........$~.......Z?................?Z.......~$...........
 * ...........=Z........7I..............I7........Z=...........
 * ............O,........+Z~..........~Z+........,O............
 * ...........:Z..........:7I,......,I7:..........Z:...........
 * ...........$+............~I=....=I~............+$...........
 * ...........:7=..........,.,=?::?=,.,..........=7:...........
 * .............=$7?===:,....:~~77~~:....,:===?7$=.............
 * ..............=Z~.:I77I==:..:II:..:==I77I:.~Z=..............
 * ...............$?..~+.=?+7I+:?+:+I$+?=.+~..?$...............
 * ...............:$+=?+~.+,,~??+I?+~,,+.~+?=+$:...............
 * .................,~$==.:......=,.,,.:.==$~,.................
 * ...................,I:~,~.:.,~+:,~~:,~:I,...................
 * ....................:I=:~.,,:=~,,..::=I:....................
 * .....................~?+?,..~==:..,?+?~.....................
 * .......................~??=:,..,:=??~.......................
 * .........................~++:,,:++~.........................
 * ...........................:++++:...........................
 * ............................................................
 *
 * https://github.com/schrodinger/fixed-data-table-2/blob/master/src/FixedDataTableCell.js#L277
 *
 * All cells require an explicit dimensions, otherwise the styles do not work.
 * - No width: borders don't apply correclty
 * - No height: empty cells render with 0 height, so vertical borders do not work.
 */
const BaseCell: React.FunctionComponent<BaseCellProps> = ({
  columnKey,
  rowIndex,
  children,
  classes,
  dataValue,
  style,
  truncate = false,
}) => {
  const elmId = [columnKey, rowIndex].join('_')

  // Derive a plain-text title for hover tooltip when content is a string.
  const title =
    typeof children === 'string' || typeof children === 'number'
      ? String(children)
      : undefined

  return (
    <Cell
      rowIndex={rowIndex}
      data-elm-id={elmId}
      className={cx('Cell', { 'u-text-truncate': truncate }, classes)}
      data-value={dataValue}
      style={style} // eslint-disable-line
      title={title}
    >
      {children}
    </Cell>
  )
}

interface BaseCellStatics {
  COLORED_CELL_OPTIONS: typeof COLORED_CELL_OPTIONS
}

const BaseCellWithStatics: React.FC<BaseCellProps> & BaseCellStatics =
  BaseCell as unknown as React.FC<BaseCellProps> & BaseCellStatics

BaseCellWithStatics.COLORED_CELL_OPTIONS = COLORED_CELL_OPTIONS

const HeaderCell: React.FunctionComponent<HeaderCellProps> = ({
  children,
  columnKey,
  isSortableColumn,
  onSortChange,

  // This gets a little circular. Shall we remove the dynamic component instances? It's here because morpheus mixes some
  // dynamic state with some static columns
  definition,
}) => {
  const initialSortDirection = definition?.colProps.initialSortDirection

  const [direction, setDirection] =
    useState<SORT_DIRECTION>(initialSortDirection)

  useEffect(() => setDirection(initialSortDirection), [initialSortDirection])

  const dataType = definition?.dataType

  const caret = useMemo(() => {
    if (!isSortableColumn) return null

    const caretDirection = direction === SORT_DIRECTION.ASC ? 'up' : 'down'

    return <Icon name={`caret ${caretDirection}`} />
  }, [isSortableColumn, direction])

  const onSort = useCallback(() => {
    if (!isSortableColumn) return null

    const newSortDirection =
      direction === SORT_DIRECTION.ASC
        ? SORT_DIRECTION.DESC
        : SORT_DIRECTION.ASC

    setDirection(newSortDirection)

    return onSortChange(
      // @ts-ignore next line
      columnKey,
      // We can't just pass the direction because the existing API wants a boolean
      newSortDirection === SORT_DIRECTION.ASC,
      // TODO: Need to check how morpheus uses this so it's backwards compat
      dataType
    )
  }, [isSortableColumn, direction, columnKey, onSortChange, dataType])

  return (
    <div className='u-full-height' onClick={onSort}>
      <BaseCell
        rowIndex={NamedRowIndex.header}
        columnKey={columnKey as string}
        classes={[
          'Cell--header',
          'u-full-height',
          isSortableColumn ? 'is-sortable' : '',
        ]}
      >
        {children}
        {caret}
      </BaseCell>
    </div>
  )
}

const TextCell = ({ value, ...rest }: CellProps) => (
  <BaseCell {...rest} children={value} />
)

interface CopyableTextCellProps extends BaseCellProps {
  value: string
}

const CopyableTextCell: React.FunctionComponent<CopyableTextCellProps> = ({
  value,
  columnKey,
  rowIndex,
}) => {
  const elmId = [columnKey, rowIndex].join('_')

  return (
    <div className={cx('copyable-cell')} data-elm-id={elmId}>
      <span className={cx('copyable-cell-text')} data-elm-id={`${elmId}_text`}>
        {value}
      </span>
      <span className={cx('copyable-cell-button')}>
        <CopyButton
          text={value}
          size='small'
          elmId={`${elmId}_copy`}
          showSuccessFeedback={false}
        />
      </span>
    </div>
  )
}

// TODO: Talk with Isaac about the thoughts behind the previous implementation of cells, with all the gazillion
// different formatOptions combinations?
//
// We need two bits of information here:
// - The format we expect the date to be in
// - The output format we wish the date to display as.
//
// Everything else is just noise.
interface DateCellProps extends CellProps {
  format?: string
}

const DateCell: React.FunctionComponent<DateCellProps> = ({
  value,
  format = date.API_DATE_FORMAT,
  ...rest
}) => {
  /* eslint-disable  @typescript-eslint/no-unsafe-assignment */
  const children = date.formatDateApiToUi(
    value ? String(value) : value,
    format as 'MM/DD/YYYY'
  )

  return <BaseCell {...rest} children={children} />
  /* eslint-enable  @typescript-eslint/no-unsafe-assignment */
}

interface EnumCellProps extends CellProps {
  colorMapper?: (
    value: CellValue | undefined
  ) => keyof typeof COLORED_CELL_OPTIONS
}

const EnumCell = (props: EnumCellProps) => {
  const defaultColorMapper = () => COLORED_CELL_OPTIONS.DEFAULT
  const { value, colorMapper = defaultColorMapper } = props

  return (
    <BaseCell {...props} classes={[cx(colorMapper(value))]}>
      {/* eslint-disable-next-line */}
      {utils.formatters.formatEnum(value?.toString())}
    </BaseCell>
  )
}

type InfoPillCellColorOption =
  (typeof COLORED_CELL_OPTIONS)[keyof typeof COLORED_CELL_OPTIONS]

interface InfoPillCellProps extends CellProps {
  colorMapper?: (value: CellValue | undefined) => InfoPillCellColorOption
}

const InfoPillCell = (props: InfoPillCellProps) => {
  const defaultColorMapper = () => COLORED_CELL_OPTIONS.DEFAULT
  const { value, colorMapper = defaultColorMapper } = props

  return (
    <BaseCell {...props}>
      <InfoPill
        // eslint-disable-next-line
        content={utils.formatters.formatEnum(value?.toString()).toUpperCase()}
        foregroundColor={colorMapper(value)}
        backgroundColor={LIGHT_COLOR_MAPPING[colorMapper(value)]}
        includeBorder={false}
        {...props}
      />
    </BaseCell>
  )
}

const PhoneCell: React.FunctionComponent<CellProps> = ({ value, ...rest }) => (
  <BaseCell {...rest} children={formatPhoneNumber(value?.toString() || '')} />
)

const HtmlCell: React.FunctionComponent<CellProps> = ({ value, ...rest }) => (
  <BaseCell {...rest} classes={['Cell--html']}>
    <section dangerouslySetInnerHTML={{ __html: (value as string) || '' }} />
  </BaseCell>
)

const PercentageCell = ({ value, ...rest }: CellProps) => {
  // Because the caller didn't check form divide by zero of null values perhaps
  if (value === Infinity || isNaN(value as number)) {
    value = '0'
  }

  return (
    <BaseCell
      {...rest}
      children={formatPercentage(value as CellValue, 0, true)}
    />
  )
}

const NumberCell = ({ value, ...rest }: CellProps) => (
  <BaseCell {...rest} children={Number(value)} />
)

interface CurrencyCellProps extends CellProps {
  withCents?: boolean
  defaultValue?: number
}

const CurrencyCell: React.FunctionComponent<CurrencyCellProps> = ({
  value,
  withCents = false,
  defaultValue = 0,
  classes,
  ...rest
}) => {
  /* eslint-disable  @typescript-eslint/no-unsafe-assignment */
  const children = withCents
    ? formatMoneyWithCents(String(value), String(defaultValue))
    : formatMoney(String(value), String(defaultValue))

  return (
    <BaseCell
      {...rest}
      classes={
        classes?.length ? ['Cell--currency', ...classes] : ['Cell--currency']
      }
      children={children}
    />
  )
  /* eslint-enable  @typescript-eslint/no-unsafe-assignment */
}

const TimeCell: React.FunctionComponent<CellProps> = ({ value, ...rest }) => (
  /* eslint-disable  @typescript-eslint/no-unsafe-assignment */
  <BaseCell {...rest} children={date.convertISOTimeToString(value as string)} />
  /* eslint-enable  @typescript-eslint/no-unsafe-assignment */
)

interface BooleanCellProps extends CellProps {
  truthyValue?: CellValue | React.ReactNode
  falsyValue?: CellValue | React.ReactNode
}

const TruthyBooleanCell: React.FC = () => (
  <div style={{ backgroundColor: '#bfdbbf' }}>{String(true)}</div>
)
const FalsyBooleanCell: React.FC = () => (
  <div style={{ backgroundColor: '#bfdbbf' }}>{String(true)}</div>
)

const BooleanCell: React.FunctionComponent<BooleanCellProps> = ({
  value,
  truthyValue = TruthyBooleanCell,
  falsyValue = FalsyBooleanCell,
  ...rest
}) => {
  const castVal = value as boolean | null

  return (
    <BaseCell
      {...rest}
      children={formatBool(
        castVal,
        truthyValue as React.ReactNode,
        falsyValue as React.ReactNode
      )}
    />
  )
}

/**
 * For Steve, from upload table implementation, instead of customFormatters and passing back and forth, forcing the text
 * cell to be something it really isn't.
 *
 * Also, notice how we don't pollute the parent container with logic that isn only required for the cell to render
 * correctly. It can also be completely defined in the app and switched out without the datatable having any idea about
 * this via an indirect reference.
 */
const statusIconMap: {
  [index: string]: { name: string; color: string; loading: boolean }
} = {
  PENDING: { name: 'hourglass-end', color: 'gray', loading: false },
  IN_PROGRESS: { name: 'sync', color: 'info', loading: true },
  COMPLETED: { name: 'check-circle', color: 'success', loading: false },
  FAILED: { name: 'times-circle', color: 'error', loading: false },
  COMPLETED_WITH_ERRORS: {
    name: 'minus-circle',
    color: 'sunYellow',
    loading: false,
  },
}

const JobStatusCell: React.FunctionComponent<CellProps> = ({
  value,
  ...rest
}) => {
  const iconProps = value ? statusIconMap[value] : null

  return (
    <BaseCell {...rest}>
      {iconProps && <Icon iconStyle='solid' {...iconProps} />}
      {value}
    </BaseCell>
  )
}

interface CheckboxCellProps {
  // TODO: This should be set as a base prop type, something like AutomatableComponent
  elmId: string
  onChange: (...args: any[]) => any
  checked: boolean
  classes?: string[]
}

const CheckboxCell: React.FunctionComponent<CheckboxCellProps> = ({
  elmId,
  onChange,
  checked,
  classes,
}) => (
  <Cell className={cx('Cell', 'Cell--checkbox', classes)}>
    <CheckboxField
      checkboxLabel={'\x00'} // force semantic to pick up clicks outside checkbox but inside cell
      elmId={elmId}
      checked={checked}
      onChange={onChange}
    />
  </Cell>
)

interface IdentifierCellProps extends CellProps {
  path: string
}

const IdentifierCell: React.FunctionComponent<IdentifierCellProps> = ({
  value,
  path,
  ...rest
}) => (
  <BaseCell {...rest}>
    <pre
      style={{ cursor: 'pointer', margin: 0 }}
      onDoubleClick={() => window.location.assign(path)}
      children={value}
    />
  </BaseCell>
)

interface RowState {
  isCollapsed: boolean
}

interface RowActions {
  toggleRowCollapse: (rowIndex: CellProps['rowIndex']) => void
}
interface CollapseActionCellProps extends CellProps {
  rowState: RowState
  rowActions: RowActions
}

const CollapseActionCell: React.FunctionComponent<CollapseActionCellProps> = ({
  rowIndex,
  columnKey,
  rowState,
  rowActions,
  ...props
}) => {
  return (
    <BaseCell columnKey={columnKey} rowIndex={rowIndex} {...props}>
      <Icon
        size='large'
        iconStyle='light'
        color='gray'
        className={cx('expand-collapse-icon', {
          active: rowState.isCollapsed,
        })}
        onClick={() => rowActions.toggleRowCollapse(rowIndex)}
        name='angle-left'
      />
    </BaseCell>
  )
}

export {
  BaseCellWithStatics as BaseCell,
  BaseCellProps,
  CollapseActionCell,
  CopyableTextCell,
  HeaderCell,
  TextCell,
  DateCell,
  PhoneCell,
  EnumCell,
  InfoPillCell,
  HtmlCell,
  PercentageCell,
  CurrencyCell,
  TimeCell,
  BooleanCell,
  JobStatusCell,
  CheckboxCell,
  IdentifierCell,
  NumberCell,
  // Only exporting here because we can use types without impacting the rest of the repo
  SORT_DIRECTION,
}
